import { DataType, FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import {
  DataProviderQueryStatus,
  type DataProviderSet,
} from '../components/DataProvider/types';
import { useTabContentConfig } from '../components/PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../components/PageConfigProvider/types/chart';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';

/**
 * Adaptive rules describe how a base chart type swaps to a different chart
 * type depending on how many entities are selected in the primary filter.
 * `shouldSwap` returns true when the adaptive target should render instead of
 * the base chart.
 */
type AdaptiveRule = {
  target: ChartType;
  shouldSwap: (entityCount: number) => boolean;
};

export const ADAPTIVE_RULES: Partial<Record<ChartType, AdaptiveRule>> = {
  bar: { target: 'metric', shouldSwap: (n) => n === 1 },
  'stacked-bar': { target: 'treemap', shouldSwap: (n) => n === 1 },
  sankey: { target: 'chord', shouldSwap: (n) => n >= 3 },
};

export const ADAPTIVE_MAP: Partial<Record<ChartType, ChartType>> =
  Object.fromEntries(
    (Object.entries(ADAPTIVE_RULES) as [ChartType, AdaptiveRule][]).map(
      ([base, rule]) => [base, rule.target]
    )
  );

type UseAdaptiveChartTypeResult = {
  chartType: ChartType | undefined;
  activePrimaryFilterLength: number;
  isAdaptiveChartMode: boolean;
};

/**
 * When item.plot.adaptive is true, the feature flag is on, and the entity
 * count matches the rule's threshold (e.g. 1 entity for bar→metric / stacked-bar→treemap,
 * 3+ entities for sankey→chord), returns the adaptive target chart type.
 * Otherwise returns the original chartType.
 */
export const useAdaptiveChartType = (
  item: PositionedItem | null
): UseAdaptiveChartTypeResult => {
  const { primaryFilter } = useTabContentConfig();
  const { activePrimaryFilterLength } = usePrimaryFilterState(
    primaryFilter?.id
  );
  const isAdaptiveChartsEnabled = useUnleashFlag(FeatureFlag.AdaptiveCharts);
  const baseChartType = item?.plot?.chartType;
  const rule = baseChartType ? ADAPTIVE_RULES[baseChartType] : undefined;
  const isAdaptiveChartMode = Boolean(
    isAdaptiveChartsEnabled && item?.plot?.adaptive && rule
  );

  if (
    isAdaptiveChartMode &&
    rule &&
    rule.shouldSwap(activePrimaryFilterLength)
  ) {
    return {
      chartType: rule.target,
      activePrimaryFilterLength,
      isAdaptiveChartMode,
    };
  }

  return {
    chartType: item?.plot?.chartType,
    activePrimaryFilterLength,
    isAdaptiveChartMode,
  };
};

const getEntityCountForAdaptiveDataSync = (
  data: DataProviderSet
): number | undefined => {
  if (
    data.type === DataType.SINGLE ||
    data.type === DataType.BREAKDOWN_SINGLE
  ) {
    return data.data.length;
  }
  return undefined;
};

export const shouldMaskAdaptiveEntityTransition = ({
  isAdaptiveChartMode,
  baseChartType,
  activePrimaryFilterLength,
  data,
  queryStatus = DataProviderQueryStatus.Success,
}: {
  isAdaptiveChartMode: boolean;
  baseChartType: ChartType;
  activePrimaryFilterLength: number;
  data: DataProviderSet;
  queryStatus?: DataProviderQueryStatus;
}): boolean => {
  if (queryStatus !== DataProviderQueryStatus.Fetching) {
    return false;
  }
  if (!isAdaptiveChartMode || activePrimaryFilterLength < 1) {
    return false;
  }
  if (!ADAPTIVE_MAP[baseChartType]) {
    return false;
  }
  const entityCount = getEntityCountForAdaptiveDataSync(data);
  if (entityCount === undefined || entityCount < 1) {
    return false;
  }
  return entityCount !== activePrimaryFilterLength;
};
